import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import PaymentForm from '../../../../../PaymentForm';
import { format } from '../../../../../utils';
import { useTheme } from '../../../../../theme';
import { getSinglePaymentPage } from '../../../../../PaymentForm/utils';
import { PaymentMethods, ExchangeOrderFragment } from '@websky/graphql';
import { useRenderers } from '../../../../../renderProps';
import SimpleLoader from '../../../../../SimpleLoader';
import { useCompletePayment } from '../../../../../PaymentForm/components/hooks';
import Notification from '../../../../../BaseComponents/Notification';

export interface Props {
	className?: string;
	exchangeOrder: ExchangeOrderFragment;
	goToOrder: () => void;
	successPayment: () => void;
	failedPayment: () => void;
	paymentMethods?: PaymentMethods;
}

const Payment: React.FC<Props> = ({
	className,
	exchangeOrder,
	goToOrder,
	successPayment,
	failedPayment,
	paymentMethods
}) => {
	const priceParts = React.useMemo(() => {
		return {
			total: exchangeOrder.prices.priceToPay,
			charge: exchangeOrder.prices.agencyChargesForExare
		};
	}, [exchangeOrder.prices]);

	const { t } = useTranslation('Exchange');
	const theme = useTheme('Exchange').Payment;

	const { completePayment, completePaymentLoading } = useCompletePayment({
		orderId: exchangeOrder.id,
		onSuccess: successPayment,
		onFail: failedPayment
	});

	const timelimitDate = new Date(Date.parse(exchangeOrder.timelimit));

	const paymentPage = React.useMemo(
		() => (paymentMethods ? getSinglePaymentPage(paymentMethods, { includeCash: true }) : null),
		[paymentMethods]
	);

	const { ExchangePaymentForm: ExchangePaymentFormOverride = PaymentForm } = useRenderers();

	return (
		<div className={cn(className, theme.container)}>
			{completePaymentLoading && <SimpleLoader />}

			<Notification
				className={theme.timelimit}
				type={'context'}
				status={'error'}
				text={t('Order must be paid by {{time}} on {{date}}', {
					time: format(timelimitDate, 'HH:mm'),
					date: format(timelimitDate, 'd MMMM yyyy')
				})}
			/>

			<ExchangePaymentFormOverride
				{...paymentMethods}
				priceToPay={priceParts}
				onSuccess={successPayment}
				onFail={failedPayment}
				paymentPage={paymentPage}
				isPaymentAvailable={!!paymentMethods}
				onBackToOrder={goToOrder}
				onPaymentMethodSelected={() => null}
				onCompletePayment={completePayment}
			/>
		</div>
	);
};

export default Payment;
